import { useEffect, useCallback, useRef } from 'react';

export const useIframeResize = (dependencies = []) => {
  const lastSentHeight = useRef(0);
  const isUpdating = useRef(false);
  const updateCount = useRef(0);

  // Function to update parent iframe height
  const updateParentHeight = useCallback(() => {
    console.log('🔧 updateParentHeight called');
    
    // Prevent rapid-fire updates
    updateCount.current++;
    if (updateCount.current > 15) { // Increased from 10 to allow for more dynamic changes
      console.log('🛑 Too many updates, stopping to prevent infinite loop');
      return;
    }
    
    // Reset counter after 3 seconds (shorter reset time for more responsive updates)
    setTimeout(() => {
      updateCount.current = 0;
    }, 3000);
    
    try {
      // Only send message if we're in an iframe
      if (window !== window.parent) {
        console.log('✅ Detected we are in an iframe');
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        
        // Add some padding to prevent any cutoff
        const adjustedHeight = height + 20;
        console.log(`📐 Calculated height: ${height}, adjusted: ${adjustedHeight}, last sent: ${lastSentHeight.current}`);
        
        // Allow both increasing AND decreasing height changes
        // Use even smaller threshold for better responsiveness: 20px
        const heightDifference = Math.abs(adjustedHeight - lastSentHeight.current);
        const isGrowingSignificantly = adjustedHeight > lastSentHeight.current && heightDifference > 20;
        const isShrinkingSignificantly = adjustedHeight < lastSentHeight.current && heightDifference > 20;
        
        if ((isGrowingSignificantly || isShrinkingSignificantly) && !isUpdating.current) {
          isUpdating.current = true;
          lastSentHeight.current = adjustedHeight;
          
          console.log(`📏 Sending height update to parent: ${adjustedHeight} (${adjustedHeight > lastSentHeight.current ? 'growing' : 'shrinking'})`);
          window.parent.postMessage({ 
            type: 'iframe-resize', 
            height: adjustedHeight,
            source: 'metricslab-calculator',
            animate: true // Add animation flag for smooth transitions
          }, '*');
          
          // Reset the updating flag after a shorter delay for more responsiveness
          setTimeout(() => {
            isUpdating.current = false;
            console.log('🔄 Reset updating flag');
          }, 500); // Reduced from 1000ms to 500ms
        } else {
          console.log(`⏭️ Skipping height update - difference: ${heightDifference}px (need >20px), growing: ${isGrowingSignificantly}, shrinking: ${isShrinkingSignificantly}, updating: ${isUpdating.current}`);
        }
      } else {
        console.log('❌ Not in an iframe, skipping height update');
      }
    } catch (error) {
      console.warn('Could not update parent height:', error);
      isUpdating.current = false;
    }
  }, []);

  // Update height when dependencies change (more responsive)
  useEffect(() => {
    console.log('🔄 Dependencies changed, scheduling height update...');
    const timeoutId = setTimeout(updateParentHeight, 300); // Reduced from 500ms for faster response
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateParentHeight, ...dependencies]);

  // Update height on component mount only
  useEffect(() => {
    console.log('🚀 useIframeResize hook mounted');
    // Initial height update
    setTimeout(updateParentHeight, 500);
    
    // Only listen for window resize, not DOM mutations to prevent loops
    const handleResize = () => {
      console.log('🖼️ Window resized, updating height...');
      setTimeout(updateParentHeight, 300);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      console.log('🛑 useIframeResize hook unmounted');
      window.removeEventListener('resize', handleResize);
    };
  }, [updateParentHeight]);

  return updateParentHeight;
}; 